import React, { useState, useEffect } from 'react';
import { AgentLayout } from '../../components/layout/AgentLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { ArrowRightLeft, Send, TrendingUp, TrendingDown, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

export const AgentTransferPage = () => {
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const [myFloat, setMyFloat] = useState(0);
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Transfer form
  const [toAgentId, setToAgentId] = useState(location.state?.toAgentId || '');
  const [toAgentName, setToAgentName] = useState(location.state?.toAgentName || '');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch my float balance
      const floatResponse = await fetch(`${API_URL}/api/agent/float`, {
        credentials: 'include'
      });

      if (floatResponse.ok) {
        const data = await floatResponse.json();
        setMyFloat(data.balance || 0);
      }

      // Fetch transfer history
      const transfersResponse = await fetch(`${API_URL}/api/agents/me/transfers`, {
        credentials: 'include'
      });

      if (transfersResponse.ok) {
        const data = await transfersResponse.json();
        setTransfers(data.transfers || []);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async (e) => {
    e.preventDefault();

    if (!toAgentId) {
      toast.error('Please select a recipient agent');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    if (parseFloat(amount) > myFloat) {
      toast.error('Insufficient float balance');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/agents/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to_user_id: toAgentId,
          amount: parseFloat(amount),
          reference: reference,
          note: note
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success('Float transferred successfully!');
        setAmount('');
        setReference('');
        setNote('');
        setToAgentId('');
        setToAgentName('');
        fetchData();
      } else {
        const error = await response.json();
        toast.error(formatApiErrorDetail(error.detail) || 'Transfer failed');
      }
    } catch (error) {
      toast.error('Transfer failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  return (
    <AgentLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ArrowRightLeft className="h-8 w-8" />
            Agent-to-Agent Transfer
          </h1>
          <p className="text-muted-foreground mt-1">
            Send float directly to other agents in the network
          </p>
        </div>

        {/* Current Balance */}
        <Card>
          <CardHeader>
            <CardTitle>Your Float Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">R {myFloat.toFixed(2)}</div>
            <Button
              variant="link"
              className="p-0 h-auto mt-2"
              onClick={() => navigate('/agent/directory')}
            >
              <Users className="h-4 w-4 mr-1" />
              Browse Agent Directory
            </Button>
          </CardContent>
        </Card>

        {/* Transfer Form */}
        <Card>
          <CardHeader>
            <CardTitle>Send Float</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleTransfer} className="space-y-4">
              <div>
                <Label>Recipient Agent ID*</Label>
                <Input
                  value={toAgentId}
                  onChange={(e) => setToAgentId(e.target.value)}
                  placeholder="Enter agent user ID"
                  required
                />
                {toAgentName && (
                  <p className="text-sm text-muted-foreground mt-1">Sending to: {toAgentName}</p>
                )}
              </div>

              <div>
                <Label>Amount (ZAR)*</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  required
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Available: R {myFloat.toFixed(2)}
                </p>
              </div>

              <div>
                <Label>Reference (Optional)</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Transaction reference"
                />
              </div>

              <div>
                <Label>Note (Optional)</Label>
                <Textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Add a note for this transfer..."
                  rows={3}
                />
              </div>

              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Processing...' : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Float
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Transfer History */}
        <Card>
          <CardHeader>
            <CardTitle>Transfer History</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-center py-8">Loading...</p>
            ) : transfers.length === 0 ? (
              <p className="text-center py-8 text-muted-foreground">No transfers yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transfers.map((transfer, idx) => {
                    const isSent = transfer.from_user_id === transfer.user_id;
                    return (
                      <TableRow key={idx}>
                        <TableCell className="text-sm">
                          {formatDate(transfer.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {isSent ? (
                              <>
                                <TrendingDown className="h-4 w-4 text-red-600" />
                                <span className="text-red-600">Sent</span>
                              </>
                            ) : (
                              <>
                                <TrendingUp className="h-4 w-4 text-green-600" />
                                <span className="text-green-600">Received</span>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="font-medium">
                            {isSent ? transfer.to_agent_name : transfer.from_agent_name}
                          </p>
                        </TableCell>
                        <TableCell>
                          <p className={`font-bold ${
                            isSent ? 'text-red-600' : 'text-green-600'
                          }`}>
                            {isSent ? '-' : '+'} R {transfer.amount?.toFixed(2)}
                          </p>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-green-500">{transfer.status}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AgentLayout>
  );
};